import * as fs from "node:fs";
import * as path from "node:path";
import {
	applyBackgroundToLine,
	type Box,
	type EditorTheme,
	type Input,
	type MarkdownTheme,
	type SelectListTheme,
	type SettingsListTheme,
	visibleWidth,
} from "@kolisachint/hoocode-tui";
import chalk from "chalk";
import { highlight, supportsLanguage } from "cli-highlight";
import { type Static, Type } from "typebox";
import { Compile } from "typebox/compile";
import { getCustomThemesDir, getThemesDir } from "../../../config.js";
import type { SourceInfo } from "../../../core/source-info.js";
import { closeWatcher, watchWithErrorHandler } from "../../../utils/fs-watch.js";

// ============================================================================
// Types & Schema
// ============================================================================

const ColorValueSchema = Type.Union([
	Type.String(), // hex "#ff0000", var ref "primary", or empty ""
	Type.Integer({ minimum: 0, maximum: 255 }), // 256-color index
]);

type ColorValue = Static<typeof ColorValueSchema>;

const ThemeJsonSchema = Type.Object({
	$schema: Type.Optional(Type.String()),
	name: Type.String(),
	description: Type.Optional(Type.String()),
	vars: Type.Optional(Type.Record(Type.String(), ColorValueSchema)),
	colors: Type.Object({
		// Core UI (10 colors)
		accent: ColorValueSchema,
		border: ColorValueSchema,
		borderAccent: ColorValueSchema,
		borderMuted: ColorValueSchema,
		success: ColorValueSchema,
		error: ColorValueSchema,
		warning: ColorValueSchema,
		muted: ColorValueSchema,
		dim: ColorValueSchema,
		text: ColorValueSchema,
		thinkingText: ColorValueSchema,
		// Backgrounds & Content Text (11 colors)
		selectedBg: ColorValueSchema,
		userMessageBg: ColorValueSchema,
		userMessageText: ColorValueSchema,
		customMessageBg: ColorValueSchema,
		customMessageText: ColorValueSchema,
		customMessageLabel: ColorValueSchema,
		toolPendingBg: ColorValueSchema,
		toolSuccessBg: ColorValueSchema,
		toolErrorBg: ColorValueSchema,
		// OPTIONAL so existing custom themes keep validating; missing entries fall
		// back to customMessageBg (see createTheme).
		warningBg: Type.Optional(ColorValueSchema),
		toolTitle: ColorValueSchema,
		toolOutput: ColorValueSchema,
		// Markdown (10 colors)
		mdHeading: ColorValueSchema,
		mdLink: ColorValueSchema,
		mdLinkUrl: ColorValueSchema,
		mdCode: ColorValueSchema,
		mdCodeBlock: ColorValueSchema,
		mdCodeBlockBorder: ColorValueSchema,
		mdQuote: ColorValueSchema,
		mdQuoteBorder: ColorValueSchema,
		mdHr: ColorValueSchema,
		mdListBullet: ColorValueSchema,
		// Tool Diffs (3 colors)
		toolDiffAdded: ColorValueSchema,
		toolDiffRemoved: ColorValueSchema,
		toolDiffContext: ColorValueSchema,
		// Syntax Highlighting (9 colors)
		syntaxComment: ColorValueSchema,
		syntaxKeyword: ColorValueSchema,
		syntaxFunction: ColorValueSchema,
		syntaxVariable: ColorValueSchema,
		syntaxString: ColorValueSchema,
		syntaxNumber: ColorValueSchema,
		syntaxType: ColorValueSchema,
		syntaxOperator: ColorValueSchema,
		syntaxPunctuation: ColorValueSchema,
		// Thinking Level Borders (6 colors)
		thinkingOff: ColorValueSchema,
		thinkingMinimal: ColorValueSchema,
		thinkingLow: ColorValueSchema,
		thinkingMedium: ColorValueSchema,
		thinkingHigh: ColorValueSchema,
		thinkingXhigh: ColorValueSchema,
		// Bash Mode (1 color)
		bashMode: ColorValueSchema,
		// Agent identity palette (6 colors). OPTIONAL so existing custom themes
		// keep validating: any missing entry falls back to `accent` (see
		// createTheme). Subagent types hash into this palette so the same agent
		// reads as the same color across the chat, task panel, and TaskOutput.
		agent1: Type.Optional(ColorValueSchema),
		agent2: Type.Optional(ColorValueSchema),
		agent3: Type.Optional(ColorValueSchema),
		agent4: Type.Optional(ColorValueSchema),
		agent5: Type.Optional(ColorValueSchema),
		agent6: Type.Optional(ColorValueSchema),
		// Brand chip (2 colors). OPTIONAL, and only honoured as a pair: a theme
		// that sets both paints the footer's brand mark as a filled chip instead
		// of accent-coloured text. Light themes need this — a brand hue bright
		// enough to read as a chip is rarely legible as text on a light canvas.
		brandBg: Type.Optional(ColorValueSchema),
		brandText: Type.Optional(ColorValueSchema),
		// Cut-out chrome (6 colors). All OPTIONAL, all with a defined fallback,
		// so a theme that sets none of them renders exactly as it does today.
		//
		// paperShadow: the offset band drawn under a filled message block, so the
		// block reads as a sheet laid on the page rather than printed into it. A
		// terminal has no sub-pixel offsets, so this is one extra row of `▔` — an
		// upper one-eighth block, a hairline along the top of its cells. Unset: no
		// shadow row is drawn.
		paperShadow: Type.Optional(ColorValueSchema),
		// halftone: the *unfilled* remainder of a gauge or progress track. It has
		// to sit apart from `dim`, which is body-weight text — a track that reads
		// as text competes with the fill it is supposed to recede behind. Unset:
		// falls back to `dim`, which is what the tracks used before.
		halftone: Type.Optional(ColorValueSchema),
		// Headline chip (2 colors), honoured only as a pair. Markdown headings
		// render as a filled bar instead of coloured text. This is how a light
		// theme keeps a vivid brand hue: inside a chip a color can be far
		// brighter than it could ever be as text on the page. Unset: headings
		// stay `mdHeading`-coloured text.
		headlineBg: Type.Optional(ColorValueSchema),
		headlineText: Type.Optional(ColorValueSchema),
		// Tape chip (2 colors), honoured only as a pair. A label laid *across* a
		// block rather than set inside it — the extension tag, a tool's name in
		// radar. Separate from brandBg/brandText so taping a label does not
		// restyle the footer's brand mark. Unset: the label keeps its own color.
		tapeBg: Type.Optional(ColorValueSchema),
		tapeText: Type.Optional(ColorValueSchema),
		// activeToolBg: the marker stroke over the most recent tool row in radar.
		// Radar is a map of a run, and a map is most useful when it says where you
		// are on it — the stroke walks down the screen as the agent works, then
		// stays on the last thing it did. Unset: no stroke, which is how radar
		// rendered before. Kept separate from `selectedBg` so a theme can make the
		// transcript's mark quieter than its list selection.
		activeToolBg: Type.Optional(ColorValueSchema),
	}),
	export: Type.Optional(
		Type.Object({
			pageBg: Type.Optional(ColorValueSchema),
			cardBg: Type.Optional(ColorValueSchema),
			infoBg: Type.Optional(ColorValueSchema),
		}),
	),
});

type ThemeJson = Static<typeof ThemeJsonSchema>;

const validateThemeJson = Compile(ThemeJsonSchema);

export type ThemeColor =
	| "accent"
	| "border"
	| "borderAccent"
	| "borderMuted"
	| "success"
	| "error"
	| "warning"
	| "muted"
	| "dim"
	| "text"
	| "thinkingText"
	| "userMessageText"
	| "customMessageText"
	| "customMessageLabel"
	| "toolTitle"
	| "toolOutput"
	| "mdHeading"
	| "mdLink"
	| "mdLinkUrl"
	| "mdCode"
	| "mdCodeBlock"
	| "mdCodeBlockBorder"
	| "mdQuote"
	| "mdQuoteBorder"
	| "mdHr"
	| "mdListBullet"
	| "toolDiffAdded"
	| "toolDiffRemoved"
	| "toolDiffContext"
	| "syntaxComment"
	| "syntaxKeyword"
	| "syntaxFunction"
	| "syntaxVariable"
	| "syntaxString"
	| "syntaxNumber"
	| "syntaxType"
	| "syntaxOperator"
	| "syntaxPunctuation"
	| "thinkingOff"
	| "thinkingMinimal"
	| "thinkingLow"
	| "thinkingMedium"
	| "thinkingHigh"
	| "thinkingXhigh"
	| "bashMode"
	| "agent1"
	| "agent2"
	| "agent3"
	| "agent4"
	| "agent5"
	| "agent6"
	| "mcp"
	| "brandText"
	| "paperShadow"
	| "halftone"
	| "headlineText"
	| "tapeText";

/** The agent identity palette, in hash order. */
export const AGENT_COLOR_TOKENS = [
	"agent1",
	"agent2",
	"agent3",
	"agent4",
	"agent5",
	"agent6",
] as const satisfies readonly ThemeColor[];

/**
 * Stable color token for an agent type ("explore", "plan", a custom agent…).
 * The name hashes into the agent palette, so the same type renders in the same
 * color everywhere it appears — chat tool line, task panel rows/roster, and
 * TaskOutput — for the whole session and across sessions. Distinct types spread
 * over 6 hues; collisions are possible but the `[name]`/`#N` labels stay the
 * primary identifier, color is the at-a-glance cue.
 */
export function agentColorFor(agentType: string): ThemeColor {
	// djb2: tiny, stable, good spread for short identifier-like strings.
	let hash = 5381;
	for (let i = 0; i < agentType.length; i++) {
		hash = ((hash << 5) + hash + agentType.charCodeAt(i)) >>> 0;
	}
	return AGENT_COLOR_TOKENS[hash % AGENT_COLOR_TOKENS.length] as ThemeColor;
}

/**
 * The colour token for a session's slot (1-6). Sessions share the agent identity
 * palette: a session chip and an agent tag never appear on the same surface, so a
 * collision costs nothing but a coincidence, and reusing the tokens keeps every
 * theme file untouched. A dedicated `session1…6` palette can be layered on later
 * without breaking anything, since this is the only place the mapping lives.
 */
export function sessionColorToken(slot: number): ThemeColor {
	const index = Number.isInteger(slot) ? slot - 1 : 0;
	const wrapped = ((index % AGENT_COLOR_TOKENS.length) + AGENT_COLOR_TOKENS.length) % AGENT_COLOR_TOKENS.length;
	return AGENT_COLOR_TOKENS[wrapped] as ThemeColor;
}

export type ThemeBg =
	| "selectedBg"
	| "userMessageBg"
	| "customMessageBg"
	| "toolPendingBg"
	| "toolSuccessBg"
	| "toolErrorBg"
	| "warningBg"
	| "brandBg"
	| "headlineBg"
	| "tapeBg"
	| "activeToolBg";

type ColorMode = "truecolor" | "256color";

// ============================================================================
// Color Utilities
// ============================================================================

function detectColorMode(): ColorMode {
	const colorterm = process.env.COLORTERM;
	if (colorterm === "truecolor" || colorterm === "24bit") {
		return "truecolor";
	}
	// Windows Terminal supports truecolor
	if (process.env.WT_SESSION) {
		return "truecolor";
	}
	const term = process.env.TERM || "";
	// Fall back to 256color for truly limited terminals
	if (term === "dumb" || term === "" || term === "linux") {
		return "256color";
	}
	// Terminal.app also doesn't support truecolor
	if (process.env.TERM_PROGRAM === "Apple_Terminal") {
		return "256color";
	}
	// GNU screen doesn't support truecolor unless explicitly opted in via COLORTERM=truecolor.
	// TERM under screen is typically "screen", "screen-256color", or "screen.xterm-256color".
	if (term === "screen" || term.startsWith("screen-") || term.startsWith("screen.")) {
		return "256color";
	}
	// Assume truecolor for everything else - virtually all modern terminals support it
	return "truecolor";
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
	const cleaned = hex.replace("#", "");
	if (cleaned.length !== 6) {
		throw new Error(`Invalid hex color: ${hex}`);
	}
	const r = parseInt(cleaned.substring(0, 2), 16);
	const g = parseInt(cleaned.substring(2, 4), 16);
	const b = parseInt(cleaned.substring(4, 6), 16);
	if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
		throw new Error(`Invalid hex color: ${hex}`);
	}
	return { r, g, b };
}

// The 6x6x6 color cube channel values (indices 0-5)
const CUBE_VALUES = [0, 95, 135, 175, 215, 255];

// Grayscale ramp values (indices 232-255, 24 grays from 8 to 238)
const GRAY_VALUES = Array.from({ length: 24 }, (_, i) => 8 + i * 10);

function findClosestCubeIndex(value: number): number {
	let minDist = Infinity;
	let minIdx = 0;
	for (let i = 0; i < CUBE_VALUES.length; i++) {
		const dist = Math.abs(value - CUBE_VALUES[i]);
		if (dist < minDist) {
			minDist = dist;
			minIdx = i;
		}
	}
	return minIdx;
}

function findClosestGrayIndex(gray: number): number {
	let minDist = Infinity;
	let minIdx = 0;
	for (let i = 0; i < GRAY_VALUES.length; i++) {
		const dist = Math.abs(gray - GRAY_VALUES[i]);
		if (dist < minDist) {
			minDist = dist;
			minIdx = i;
		}
	}
	return minIdx;
}

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
	// Weighted Euclidean distance (human eye is more sensitive to green)
	const dr = r1 - r2;
	const dg = g1 - g2;
	const db = b1 - b2;
	return dr * dr * 0.299 + dg * dg * 0.587 + db * db * 0.114;
}

function rgbTo256(r: number, g: number, b: number): number {
	// Find closest color in the 6x6x6 cube
	const rIdx = findClosestCubeIndex(r);
	const gIdx = findClosestCubeIndex(g);
	const bIdx = findClosestCubeIndex(b);
	const cubeR = CUBE_VALUES[rIdx];
	const cubeG = CUBE_VALUES[gIdx];
	const cubeB = CUBE_VALUES[bIdx];
	const cubeIndex = 16 + 36 * rIdx + 6 * gIdx + bIdx;
	const cubeDist = colorDistance(r, g, b, cubeR, cubeG, cubeB);

	// Find closest grayscale
	const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
	const grayIdx = findClosestGrayIndex(gray);
	const grayValue = GRAY_VALUES[grayIdx];
	const grayIndex = 232 + grayIdx;
	const grayDist = colorDistance(r, g, b, grayValue, grayValue, grayValue);

	// Check if color has noticeable saturation (hue matters)
	// If max-min spread is significant, prefer cube to preserve tint
	const maxC = Math.max(r, g, b);
	const minC = Math.min(r, g, b);
	const spread = maxC - minC;

	// Only consider grayscale if color is nearly neutral (spread < 10)
	// AND grayscale is actually closer
	if (spread < 10 && grayDist < cubeDist) {
		return grayIndex;
	}

	return cubeIndex;
}

function hexTo256(hex: string): number {
	const { r, g, b } = hexToRgb(hex);
	return rgbTo256(r, g, b);
}

function fgAnsi(color: string | number, mode: ColorMode): string {
	if (color === "") return "\x1b[39m";
	if (typeof color === "number") return `\x1b[38;5;${color}m`;
	if (color.startsWith("#")) {
		if (mode === "truecolor") {
			const { r, g, b } = hexToRgb(color);
			return `\x1b[38;2;${r};${g};${b}m`;
		} else {
			const index = hexTo256(color);
			return `\x1b[38;5;${index}m`;
		}
	}
	throw new Error(`Invalid color value: ${color}`);
}

function bgAnsi(color: string | number, mode: ColorMode): string {
	if (color === "") return "\x1b[49m";
	if (typeof color === "number") return `\x1b[48;5;${color}m`;
	if (color.startsWith("#")) {
		if (mode === "truecolor") {
			const { r, g, b } = hexToRgb(color);
			return `\x1b[48;2;${r};${g};${b}m`;
		} else {
			const index = hexTo256(color);
			return `\x1b[48;5;${index}m`;
		}
	}
	throw new Error(`Invalid color value: ${color}`);
}

/** The two inks a chip can be written in: the darker one, and the lighter one. */
const CHIP_INK_DARK = "#0b0b0f";
const CHIP_INK_LIGHT = "#ffffff";

/**
 * WCAG contrast between two resolved colours, or undefined if either is
 * unparseable.
 */
function contrastRatio(a: string | number, b: string | number): number | undefined {
	const [first, second] = [relativeLuminance(a), relativeLuminance(b)];
	if (first === undefined || second === undefined) return undefined;
	return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/**
 * The ink to lay on a chip filled with `color`, and how well it reads there —
 * or undefined when the fill's luminance cannot be worked out and we would be
 * guessing at legibility.
 *
 * Chips are filled with palette hues that differ wildly between themes — a dark
 * theme's agent1 is a bright cyan, a light theme's is a deep teal meant as ink on
 * paper — so a fixed text colour is unreadable in one theme or the other. The
 * ink is therefore whichever of the two actually measures better against this
 * fill, rather than whichever a fixed luminance cutoff nominates: near the
 * crossover the cutoff and the measurement disagree by a hair, and a hair is the
 * difference between a name that reads and one that does not.
 */
function fillInk(color: string | number): { ink: string; ratio: number } | undefined {
	const dark = contrastRatio(color, CHIP_INK_DARK);
	const light = contrastRatio(color, CHIP_INK_LIGHT);
	if (dark === undefined || light === undefined) return undefined;
	return dark >= light ? { ink: CHIP_INK_DARK, ratio: dark } : { ink: CHIP_INK_LIGHT, ratio: light };
}

/**
 * How light a chip fill has to be before its hue reads as that hue.
 *
 * A colour has two jobs in a light theme and they pull in opposite directions.
 * As *text* on paper it has to clear 4.5:1, which forces it dark — and a dark
 * yellow is brown, a dark cyan is navy, a dark green is bottle. As a *fill* it
 * only has to out-contrast the ink laid on it, which leaves it free to be the
 * hue it is named after. Lifting the fill to a floor lightness settles the
 * argument: the token stays ink where it is drawn as text, and the chip built
 * from it is recognisably yellow, cyan, green.
 *
 * 0.5 is where a fully saturated hue stops reading as a shade of brown and starts
 * reading as itself.
 */
const MIN_CHIP_FILL_LIGHTNESS = 0.5;

/**
 * How well the name written on a chip has to read against the fill under it.
 *
 * Lightness alone is not a legibility test, because hues do not carry the same
 * brightness at the same lightness: a blue or a violet at 0.5 lands near
 * luminance 0.2, which is the crossover where dark ink and light ink are
 * *equally poor* — around 4.3:1 either way, under the 4.5:1 floor for body text
 * and well under what a two-word name on a 22-cell chip wants. Solarized Dark's
 * violet and magenta slots sat exactly there, which is what made a session's own
 * name the hardest thing on its input box to read.
 *
 * So the fill is measured rather than guessed at: it is moved until the better
 * of the two inks clears this bar. 5.5 leaves real headroom over AA without
 * forcing every chip to the top of the lightness range, where six hues start
 * looking like six pastels.
 */
const MIN_CHIP_INK_CONTRAST = 5.5;

/**
 * The same bar, as a 256-colour terminal is able to keep it.
 *
 * Apple Terminal, GNU screen and TERM=linux round every fill to the nearest
 * entry of a 6×6×6 cube, and that rounding moves luminance. Holding the rounded
 * fill to the full 5.5 would mean lifting until it lands in a *different* cube
 * cell — and the cells these palettes round into are shared, so the lift that
 * bought contrast spent the thing the chip is for: Solarized's violet and blue
 * slots came out as the same swatch. The rounded fill answers to AA for body
 * text instead, which is a floor it can clear without leaving its own cell.
 */
const MIN_CHIP_INK_CONTRAST_QUANTIZED = 4.5;

/**
 * Lightness past which lifting stops, so a hue is never bleached to paper.
 *
 * Nothing in the shipped palettes gets anywhere near it — the deepest ink in
 * them settles by 0.55. It is the backstop for a custom theme whose palette
 * entry is so dark that the walk would otherwise run to white.
 */
const MAX_CHIP_FILL_LIGHTNESS = 0.88;

/**
 * Saturation a lifted fill is held under.
 *
 * Lifting at the token's own saturation is what turns a light theme's deep
 * inks into highlighter pen: `#00382d` (a bottle teal) at full saturation and
 * half lightness is `#00ffcd`, and `#035500` is `#09ff00`. Those are nobody's
 * idea of the theme's green. Pulling a lifted fill back to this ceiling is the
 * same move as mixing the ink with white — the hue survives, the fluorescence
 * does not. Only *lifted* fills are capped: a palette entry bright enough to be
 * its own chip is left exactly as the theme wrote it.
 */
const MAX_CHIP_FILL_SATURATION = 0.72;

/** Step the lift walks in. Small enough that a fill never overshoots visibly. */
const CHIP_FILL_LIGHTNESS_STEP = 0.02;

/** HSL of a hex colour, in 0-360 / 0-1 / 0-1. */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
	const { r, g, b } = hexToRgb(hex);
	const [red, green, blue] = [r / 255, g / 255, b / 255];
	const max = Math.max(red, green, blue);
	const min = Math.min(red, green, blue);
	const delta = max - min;
	const l = (max + min) / 2;
	if (delta === 0) return { h: 0, s: 0, l };
	const raw =
		max === red
			? (green - blue) / delta + (green < blue ? 6 : 0)
			: max === green
				? (blue - red) / delta + 2
				: (red - green) / delta + 4;
	return { h: raw * 60, s: delta / (1 - Math.abs(2 * l - 1)), l };
}

function hslToHex(h: number, s: number, l: number): string {
	const chroma = (1 - Math.abs(2 * l - 1)) * s;
	const sector = ((((h % 360) + 360) % 360) / 60) % 6;
	const second = chroma * (1 - Math.abs((sector % 2) - 1));
	const [r, g, b] = (
		sector < 1
			? [chroma, second, 0]
			: sector < 2
				? [second, chroma, 0]
				: sector < 3
					? [0, chroma, second]
					: sector < 4
						? [0, second, chroma]
						: sector < 5
							? [second, 0, chroma]
							: [chroma, 0, second]
	) as [number, number, number];
	const offset = l - chroma / 2;
	return `#${[r, g, b]
		.map((channel) =>
			Math.round((channel + offset) * 255)
				.toString(16)
				.padStart(2, "0"),
		)
		.join("")}`;
}

/**
 * What a colour looks like once the terminal has actually painted it.
 *
 * On a 256-colour terminal every fill is rounded to the nearest cube entry
 * first, and that rounding moves luminance — so an ink chosen against the
 * unrounded hex is an ink chosen against a colour nobody sees. Measuring the
 * rounded value instead keeps the chip's contrast honest on Apple Terminal,
 * GNU screen and TERM=linux, which is where it was least honest before.
 */
function renderedColor(color: string | number, mode: ColorMode): string | number {
	if (mode !== "256color") return color;
	if (typeof color === "number") return color;
	if (!color.startsWith("#")) return color;
	try {
		return ansi256ToHex(hexTo256(color));
	} catch {
		return color;
	}
}

/**
 * The hue family a chip keeps white ink for.
 *
 * Slot 4 is magenta in every shipped theme (`/color magenta`, and the slot
 * order is a contract — see `docs/themes.md`), and magenta with a dark name on
 * it reads as pink with small print: the lift that makes dark ink legible is
 * exactly what bleaches the hue out of it. A deep magenta with a white name is
 * both more legible and more itself, so a magenta fill is deepened — never
 * lifted — until white clears the same bars every other fill answers to. When
 * no deep variant clears, the lift below still runs: legibility outranks ink.
 *
 * The window covers every shipped slot-4 token (325°–340°) with room on both
 * sides, and stops short of purple (nearest 293°) and red. The saturation gate
 * keeps out grays, whose hue is noise.
 */
export const MAGENTA_HUE_MIN = 305;
export const MAGENTA_HUE_MAX = 355;
export const MIN_MAGENTA_SATURATION = 0.25;

/**
 * Darkest a magenta fill is deepened to in search of white-clearing ink.
 *
 * A chip is a mark on the page in both polarities, so the walk stops well
 * above the terminal's own ground — a fill here is still unmistakably lit on
 * the darkest shipped background.
 */
const MIN_MAGENTA_FILL_LIGHTNESS = 0.3;

/** HSL of a palette entry known to be a saturated magenta, or undefined. */
function magentaHsl(color: string | number): { h: number; s: number; l: number } | undefined {
	const hex = typeof color === "number" ? ansi256ToHex(color) : color;
	if (!hex.startsWith("#")) return undefined;
	let hsl: { h: number; s: number; l: number };
	try {
		hsl = hexToHsl(hex);
	} catch {
		return undefined;
	}
	if (hsl.s <= MIN_MAGENTA_SATURATION) return undefined;
	const hue = ((hsl.h % 360) + 360) % 360;
	if (hue < MAGENTA_HUE_MIN || hue > MAGENTA_HUE_MAX) return undefined;
	return { h: hue, s: hsl.s, l: hsl.l };
}

/** Whether white ink on `candidate` clears both the exact and the rounded bar. */
function clearsWithWhite(candidate: string | number, mode: ColorMode): boolean {
	const exact = contrastRatio(candidate, CHIP_INK_LIGHT);
	const rounded = contrastRatio(renderedColor(candidate, mode), CHIP_INK_LIGHT);
	return (exact ?? 0) >= MIN_CHIP_INK_CONTRAST && (rounded ?? 0) >= MIN_CHIP_INK_CONTRAST_QUANTIZED;
}

/**
 * The colour a chip is actually filled with for a palette entry.
 *
 * Two things can be wrong with a palette entry used as a fill, and a theme's own
 * light/dark polarity does not decide which:
 *
 *  - On a light theme the entry is *ink* — dark enough to read on paper — and
 *    ink laid down as a swatch is a smudge whose hue nobody can name. That is
 *    what `MIN_CHIP_FILL_LIGHTNESS` answers.
 *  - On either polarity the entry can land in the luminance band where neither
 *    ink reads on it. That is what `MIN_CHIP_INK_CONTRAST` answers, and it is
 *    the case the old code did not check at all on a dark theme — which is
 *    precisely where Solarized Dark's violet and magenta chips lived.
 *
 * So the lift runs for every theme, and it runs against the *rendered* fill, but
 * it only moves a fill that has one of those problems: an entry already bright
 * enough to carry its ink comes back exactly as the theme wrote it. Only
 * lightness and (once lifted) saturation move; the hue is always the theme's, so
 * a lifted fill still reads as that theme's colour rather than a generic one.
 */
function chipFill(color: string | number, lightBackdrop: boolean, mode: ColorMode): string | number {
	// Magenta keeps white ink (see MAGENTA_HUE_MIN): an entry that already
	// carries white is used exactly as the theme wrote it, on either polarity —
	// no lightness floor, which is what bleached deep magentas into pink. One
	// that does not is deepened at its own hue until white clears, so the chip
	// stays magenta instead of becoming the pink the lift would make it.
	const magenta = magentaHsl(color);
	if (magenta) {
		if (clearsWithWhite(color, mode)) return color;
		for (
			let lightness = magenta.l - CHIP_FILL_LIGHTNESS_STEP;
			lightness >= MIN_MAGENTA_FILL_LIGHTNESS;
			lightness -= CHIP_FILL_LIGHTNESS_STEP
		) {
			// Capped like a lifted fill: a moved fill is the theme's no longer,
			// and full saturation at these lightnesses is neon either way.
			const candidate = hslToHex(magenta.h, Math.min(magenta.s, MAX_CHIP_FILL_SATURATION), lightness);
			if (clearsWithWhite(candidate, mode)) return candidate;
		}
		// No deep variant clears — fall through to the lift, which buys
		// legibility with dark ink over an illegible magenta with white.
	}

	// Both the colour asked for and the colour the terminal will round it to have
	// to carry the ink — the first to the full bar, the second to the one it can
	// hold without being pushed out of its own cube cell.
	const clears = (candidate: string | number) =>
		(fillInk(candidate)?.ratio ?? 0) >= MIN_CHIP_INK_CONTRAST &&
		(fillInk(renderedColor(candidate, mode))?.ratio ?? 0) >= MIN_CHIP_INK_CONTRAST_QUANTIZED;
	// The hue floor only applies where the palette is ink. A dark theme's entry
	// that already carries its ink is left alone however dark it is.
	if (!lightBackdrop && clears(color)) return color;

	const hex = typeof color === "number" ? ansi256ToHex(color) : color;
	if (!hex.startsWith("#")) return color;
	let hsl: { h: number; s: number; l: number };
	try {
		hsl = hexToHsl(hex);
	} catch {
		return color;
	}

	// A gray has no hue to rescue, so on a light theme there is nothing to lift it
	// for — but a mid-gray still sits in the band where neither ink reads, and
	// that has to be fixed wherever it happens.
	const floor = lightBackdrop && hsl.s > 0 ? Math.max(hsl.l, MIN_CHIP_FILL_LIGHTNESS) : hsl.l;
	const saturation = floor > hsl.l ? Math.min(hsl.s, MAX_CHIP_FILL_SATURATION) : hsl.s;

	// Up from the floor a step at a time, stopping as soon as the ink has room.
	// Lifting rather than darkening because a chip is a mark on a page in both
	// polarities: a fill darker than the terminal's own ground stops being one.
	for (let lightness = floor; lightness <= MAX_CHIP_FILL_LIGHTNESS; lightness += CHIP_FILL_LIGHTNESS_STEP) {
		const candidate = lightness <= hsl.l && saturation === hsl.s ? color : hslToHex(hsl.h, saturation, lightness);
		if (clears(candidate)) return candidate;
	}
	// Nothing in range carried the ink — take the top of the range, which is the
	// brightest the hue is allowed to get, over leaving the chip illegible.
	return hslToHex(hsl.h, saturation, MAX_CHIP_FILL_LIGHTNESS);
}

/**
 * Whether the theme paints on paper rather than on a dark terminal, judged from
 * the surfaces it defines rather than from its name. `userMessageBg` is the one
 * surface every theme sets, so it is the reliable witness.
 */
function hasLightSurfaces(bgColors: Record<ThemeBg, string | number>): boolean {
	for (const candidate of [bgColors.userMessageBg, bgColors.selectedBg]) {
		if (candidate === undefined || candidate === "") continue;
		const luminance = relativeLuminance(candidate);
		if (luminance !== undefined) return luminance > 0.5;
	}
	return false;
}

function resolveVarRefs(
	value: ColorValue,
	vars: Record<string, ColorValue>,
	visited = new Set<string>(),
): string | number {
	if (typeof value === "number" || value === "" || value.startsWith("#")) {
		return value;
	}
	if (visited.has(value)) {
		throw new Error(`Circular variable reference detected: ${value}`);
	}
	if (!(value in vars)) {
		throw new Error(`Variable reference not found: ${value}`);
	}
	visited.add(value);
	return resolveVarRefs(vars[value], vars, visited);
}

function resolveThemeColors<T extends Record<string, ColorValue>>(
	colors: T,
	vars: Record<string, ColorValue> = {},
): Record<keyof T, string | number> {
	const resolved: Record<string, string | number> = {};
	for (const [key, value] of Object.entries(colors)) {
		resolved[key] = resolveVarRefs(value, vars);
	}
	return resolved as Record<keyof T, string | number>;
}

// ============================================================================
// Theme Class
// ============================================================================

export class Theme {
	readonly name?: string;
	readonly sourcePath?: string;
	sourceInfo?: SourceInfo;
	private fgColors: Map<ThemeColor, string>;
	private bgColors: Map<ThemeBg, string>;
	/** Foreground tokens usable as a chip fill: background ANSI plus a legible ink. */
	private fillColors: Map<ThemeColor, string>;
	private mode: ColorMode;

	constructor(
		fgColors: Record<ThemeColor, string | number>,
		bgColors: Record<ThemeBg, string | number>,
		mode: ColorMode,
		options: { name?: string; sourcePath?: string; sourceInfo?: SourceInfo } = {},
	) {
		this.name = options.name;
		this.sourcePath = options.sourcePath;
		this.sourceInfo = options.sourceInfo;
		this.mode = mode;
		// Fallbacks are applied to the *raw* values rather than to the encoded
		// ANSI, because a fill has to be derived from the colour itself: it needs
		// the same value as both a background and a luminance to pick ink from.
		const raw = new Map<ThemeColor, string | number>(Object.entries(fgColors) as [ThemeColor, string | number][]);
		// Agent palette fallback for themes built before it existed (including
		// Theme instances constructed directly by extensions): default to accent.
		const accentRaw = raw.get("accent");
		if (accentRaw !== undefined) {
			for (const token of AGENT_COLOR_TOKENS) {
				if (!raw.has(token)) {
					raw.set(token, accentRaw);
				}
			}
		}
		// A gauge always has an unfilled remainder to draw, so unlike the chip
		// pairs there is no "skip it" rendering to fall back to. `dim` is what
		// the tracks used before the token existed.
		const dimRaw = raw.get("dim");
		if (dimRaw !== undefined && !raw.has("halftone")) {
			raw.set("halftone", dimRaw);
		}
		this.fgColors = new Map();
		this.fillColors = new Map();
		// A light theme's palette is ink, and ink does not read as its own hue when
		// it is laid down as a swatch, so a chip fill is lifted off it.
		const lightBackdrop = hasLightSurfaces(bgColors);
		for (const [key, value] of raw) {
			this.fgColors.set(key, fgAnsi(value, mode));
			// "" means the terminal's own foreground: there is no colour to fill
			// with, so the token simply is not fillable.
			const fill = value === "" ? value : chipFill(value, lightBackdrop, mode);
			// The ink answers to what the terminal will actually paint, which on a
			// 256-colour terminal is the rounded fill rather than the exact one.
			const ink = fill === "" ? undefined : fillInk(renderedColor(fill, mode));
			if (ink !== undefined) {
				this.fillColors.set(key, bgAnsi(fill, mode) + fgAnsi(ink.ink, mode));
			}
		}
		this.bgColors = new Map();
		for (const [key, value] of Object.entries(bgColors) as [ThemeBg, string | number][]) {
			this.bgColors.set(key, bgAnsi(value, mode));
		}
	}

	fg(color: ThemeColor, text: string): string {
		const ansi = this.fgColors.get(color);
		if (!ansi) throw new Error(`Unknown theme color: ${color}`);
		return `${ansi}${text}\x1b[39m`; // Reset only foreground color
	}

	bg(color: ThemeBg, text: string): string {
		const ansi = this.bgColors.get(color);
		if (!ansi) throw new Error(`Unknown theme background color: ${color}`);
		return `${ansi}${text}\x1b[49m`; // Reset only background color
	}

	/**
	 * Paint `text` as a filled chip in a *foreground* token — the palette lives on
	 * the foreground side, so `bg()`, which only knows the handful of surface
	 * tokens, cannot do this. The ink comes from the fill's own luminance, which
	 * is what lets one call render correctly on both a dark theme's bright hues
	 * and a light theme's deep ones.
	 *
	 * Falls back to coloured text when the token is not fillable (see `canFill`):
	 * a chip that loses its fill still reads, and callers that want the quieter
	 * outline form should ask `canFill` first.
	 */
	fill(color: ThemeColor, text: string): string {
		const ansi = this.fillColors.get(color);
		if (!ansi) return this.fg(color, text);
		return `${ansi}${text}\x1b[39m\x1b[49m`;
	}

	/** Whether `color` can be laid down as a fill with legible ink over it. */
	canFill(color: ThemeColor): boolean {
		return this.fillColors.has(color);
	}

	/** Whether the theme defines an optional token, so callers can pick a
	 * fallback rendering instead of letting fg()/bg() throw. */
	has(color: ThemeColor): boolean {
		return this.fgColors.has(color);
	}

	hasBg(color: ThemeBg): boolean {
		return this.bgColors.has(color);
	}

	bold(text: string): string {
		return chalk.bold(text);
	}

	blink(text: string): string {
		// Terminal-native blink (SGR 5). Zero overhead; terminals that support
		// blink will animate, others render it as a steady glyph.
		return `\x1b[5m${text}\x1b[25m`;
	}

	italic(text: string): string {
		return chalk.italic(text);
	}

	underline(text: string): string {
		return chalk.underline(text);
	}

	inverse(text: string): string {
		return chalk.inverse(text);
	}

	strikethrough(text: string): string {
		return chalk.strikethrough(text);
	}

	getFgAnsi(color: ThemeColor): string {
		const ansi = this.fgColors.get(color);
		if (!ansi) throw new Error(`Unknown theme color: ${color}`);
		return ansi;
	}

	getBgAnsi(color: ThemeBg): string {
		const ansi = this.bgColors.get(color);
		if (!ansi) throw new Error(`Unknown theme background color: ${color}`);
		return ansi;
	}

	getColorMode(): ColorMode {
		return this.mode;
	}

	getThinkingBorderColor(level: "off" | "minimal" | "low" | "medium" | "high" | "xhigh"): (str: string) => string {
		// Map thinking levels to dedicated theme colors
		switch (level) {
			case "off":
				return (str: string) => this.fg("thinkingOff", str);
			case "minimal":
				return (str: string) => this.fg("thinkingMinimal", str);
			case "low":
				return (str: string) => this.fg("thinkingLow", str);
			case "medium":
				return (str: string) => this.fg("thinkingMedium", str);
			case "high":
				return (str: string) => this.fg("thinkingHigh", str);
			case "xhigh":
				return (str: string) => this.fg("thinkingXhigh", str);
			default:
				return (str: string) => this.fg("thinkingOff", str);
		}
	}

	getBashModeBorderColor(): (str: string) => string {
		return (str: string) => this.fg("bashMode", str);
	}
}

// ============================================================================
// Theme Loading
// ============================================================================

let BUILTIN_THEMES: Record<string, ThemeJson> | undefined;

/** Files in the built-in theme directory that are not themes. */
const NON_THEME_FILES = new Set(["theme-schema.json"]);

function getBuiltinThemes(): Record<string, ThemeJson> {
	if (!BUILTIN_THEMES) {
		// Every *.json in the shipped theme directory is a built-in, so adding a
		// theme is a matter of dropping the file in (both build steps copy the
		// whole directory). `dark` and `light` are still the detection defaults.
		const themesDir = getThemesDir();
		const themes: Record<string, ThemeJson> = {};
		for (const file of fs.readdirSync(themesDir).sort()) {
			if (!file.endsWith(".json") || NON_THEME_FILES.has(file)) continue;
			const name = file.slice(0, -5);
			themes[name] = parseThemeJsonContent(file, fs.readFileSync(path.join(themesDir, file), "utf-8"));
		}
		BUILTIN_THEMES = themes;
	}
	return BUILTIN_THEMES;
}

/** True for themes shipped with the package, which live outside the custom themes dir. */
function isBuiltinTheme(name: string): boolean {
	return name in getBuiltinThemes();
}

/**
 * Built-ins that were retired, and what each one resolves to now.
 *
 * The shipped set is eight — four light, four dark — because a picker of
 * fourteen is a picker nobody reads to the end of, and half of those fourteen
 * were a second opinion on a theme already in the list. Retiring a name is not
 * the same as taking it away, though: somebody has `"theme": "warm-light"` in
 * their settings, and the loader's own failure path falls back to `dark`, which
 * would flip a light terminal to a dark theme on next launch.
 *
 * So a retired name still loads — as its nearest surviving relative, matched on
 * the thing that actually mattered about it: the accessibility pair keeps its
 * AAA floor (and gains colour-blind-safe hues), the `vox-*` pair keeps its
 * explanatory-journalism voice at the cut-out pair's stronger contrast, and
 * every one of them stays on the side of the light/dark split it was on.
 *
 * This is the last resort, consulted only after built-ins, extension-registered
 * themes and the user's own `themes/` directory have all been asked — so
 * somebody who keeps a copy of `warm-light.json` of their own still gets theirs.
 */
const RETIRED_THEMES: Readonly<Record<string, string>> = {
	"high-contrast-dark": "colorsafe-dark",
	"high-contrast-light": "colorsafe-light",
	"warm-dark": "colorsafe-dark",
	"warm-light": "colorsafe-light",
	"vox-dark": "vox-cutout-dark",
	"vox-light": "vox-cutout-light",
};

/** The theme a retired name now stands for, or undefined if the name is not retired. */
export function successorThemeFor(name: string): string | undefined {
	const successor = RETIRED_THEMES[name];
	return successor !== undefined && successor in getBuiltinThemes() ? successor : undefined;
}

/**
 * The theme `name` actually names today: itself if anything still defines it,
 * its successor if we retired it, and itself again if it is simply unknown —
 * the caller's own not-found handling is the right answer for that.
 */
export function resolveThemeName(name: string): string {
	if (getAvailableThemes().includes(name)) return name;
	return successorThemeFor(name) ?? name;
}

export function getAvailableThemes(): string[] {
	const themes = new Set<string>(Object.keys(getBuiltinThemes()));
	const customThemesDir = getCustomThemesDir();
	if (fs.existsSync(customThemesDir)) {
		const files = fs.readdirSync(customThemesDir);
		for (const file of files) {
			if (file.endsWith(".json")) {
				themes.add(file.slice(0, -5));
			}
		}
	}
	for (const name of registeredThemes.keys()) {
		themes.add(name);
	}
	return Array.from(themes).sort();
}

/**
 * One-line description from a theme file, shown next to the name in the theme
 * picker. Undefined for themes that don't declare one, and for themes
 * registered as instances by extensions.
 */
export function getThemeDescription(name: string): string | undefined {
	try {
		return loadThemeJson(name).description;
	} catch {
		return undefined;
	}
}

export interface ThemeInfo {
	name: string;
	path: string | undefined;
}

export function getAvailableThemesWithPaths(): ThemeInfo[] {
	const themesDir = getThemesDir();
	const customThemesDir = getCustomThemesDir();
	const result: ThemeInfo[] = [];

	// Built-in themes
	for (const name of Object.keys(getBuiltinThemes())) {
		result.push({ name, path: path.join(themesDir, `${name}.json`) });
	}

	// Custom themes
	if (fs.existsSync(customThemesDir)) {
		for (const file of fs.readdirSync(customThemesDir)) {
			if (file.endsWith(".json")) {
				const name = file.slice(0, -5);
				if (!result.some((t) => t.name === name)) {
					result.push({ name, path: path.join(customThemesDir, file) });
				}
			}
		}
	}

	for (const [name, theme] of registeredThemes.entries()) {
		if (!result.some((t) => t.name === name)) {
			result.push({ name, path: theme.sourcePath });
		}
	}

	return result.sort((a, b) => a.name.localeCompare(b.name));
}

function parseThemeJson(label: string, json: unknown): ThemeJson {
	if (!validateThemeJson.Check(json)) {
		const errors = Array.from(validateThemeJson.Errors(json));
		const missingColors = new Set<string>();
		const otherErrors: string[] = [];

		for (const error of errors) {
			if (error.keyword === "required" && error.instancePath === "/colors") {
				const requiredProperties = (error.params as { requiredProperties?: string[] }).requiredProperties;
				for (const requiredProperty of requiredProperties ?? []) {
					missingColors.add(requiredProperty);
				}
				continue;
			}

			const path = error.instancePath || "/";
			otherErrors.push(`  - ${path}: ${error.message}`);
		}

		let errorMessage = `Invalid theme "${label}":\n`;
		if (missingColors.size > 0) {
			errorMessage += "\nMissing required color tokens:\n";
			errorMessage += Array.from(missingColors)
				.sort()
				.map((color) => `  - ${color}`)
				.join("\n");
			errorMessage += '\n\nPlease add these colors to your theme\'s "colors" object.';
			errorMessage += "\nSee the built-in themes (dark.json, light.json) for reference values.";
		}
		if (otherErrors.length > 0) {
			errorMessage += `\n\nOther errors:\n${otherErrors.join("\n")}`;
		}

		throw new Error(errorMessage);
	}

	return json as ThemeJson;
}

function parseThemeJsonContent(label: string, content: string): ThemeJson {
	let json: unknown;
	try {
		json = JSON.parse(content);
	} catch (error) {
		throw new Error(`Failed to parse theme ${label}: ${error}`);
	}
	return parseThemeJson(label, json);
}

function loadThemeJson(name: string): ThemeJson {
	const builtinThemes = getBuiltinThemes();
	if (name in builtinThemes) {
		return builtinThemes[name];
	}
	const registeredTheme = registeredThemes.get(name);
	if (registeredTheme?.sourcePath) {
		const content = fs.readFileSync(registeredTheme.sourcePath, "utf-8");
		return parseThemeJsonContent(registeredTheme.sourcePath, content);
	}
	if (registeredTheme) {
		throw new Error(`Theme "${name}" does not have a source path for export`);
	}
	const customThemesDir = getCustomThemesDir();
	const themePath = path.join(customThemesDir, `${name}.json`);
	if (!fs.existsSync(themePath)) {
		// Nobody defines this name any more. If we retired it ourselves, hand back
		// what it was retired in favour of rather than letting the caller fall all
		// the way back to `dark` — see RETIRED_THEMES.
		const successor = successorThemeFor(name);
		if (successor) return builtinThemes[successor];
		throw new Error(`Theme not found: ${name}`);
	}
	const content = fs.readFileSync(themePath, "utf-8");
	return parseThemeJsonContent(name, content);
}

function createTheme(themeJson: ThemeJson, mode?: ColorMode, sourcePath?: string): Theme {
	const colorMode = mode ?? detectColorMode();
	const resolvedColors = resolveThemeColors(themeJson.colors, themeJson.vars);
	const fgColors: Record<ThemeColor, string | number> = {} as Record<ThemeColor, string | number>;
	const bgColors: Record<ThemeBg, string | number> = {} as Record<ThemeBg, string | number>;
	const bgColorKeys: Set<string> = new Set([
		"selectedBg",
		"userMessageBg",
		"customMessageBg",
		"toolPendingBg",
		"toolSuccessBg",
		"toolErrorBg",
		"warningBg",
		"brandBg",
		"headlineBg",
		"tapeBg",
		"activeToolBg",
	]);
	for (const [key, value] of Object.entries(resolvedColors)) {
		if (bgColorKeys.has(key)) {
			bgColors[key as ThemeBg] = value;
		} else {
			fgColors[key as ThemeColor] = value;
		}
	}
	// The agent palette is optional in theme JSON (older custom themes predate
	// it); missing entries fall back to accent so theme.fg never throws.
	for (const token of AGENT_COLOR_TOKENS) {
		if (fgColors[token] === undefined) {
			fgColors[token] = fgColors.accent;
		}
	}
	// Same story for the warning surface: custom themes predate it, and the
	// billing notice needs *some* fill rather than a second render path.
	if (bgColors.warningBg === undefined) {
		bgColors.warningBg = bgColors.customMessageBg;
	}
	return new Theme(fgColors, bgColors, colorMode, {
		name: themeJson.name,
		sourcePath,
	});
}

export function loadThemeFromPath(themePath: string, mode?: ColorMode): Theme {
	const content = fs.readFileSync(themePath, "utf-8");
	const themeJson = parseThemeJsonContent(themePath, content);
	return createTheme(themeJson, mode, themePath);
}

function loadTheme(name: string, mode?: ColorMode): Theme {
	const registeredTheme = registeredThemes.get(name);
	if (registeredTheme) {
		return registeredTheme;
	}
	const themeJson = loadThemeJson(name);
	return createTheme(themeJson, mode);
}

export function getThemeByName(name: string): Theme | undefined {
	try {
		return loadTheme(name);
	} catch {
		return undefined;
	}
}

function detectTerminalBackground(): "dark" | "light" {
	const colorfgbg = process.env.COLORFGBG || "";
	if (colorfgbg) {
		const parts = colorfgbg.split(";");
		if (parts.length >= 2) {
			const bg = parseInt(parts[1], 10);
			if (!Number.isNaN(bg)) {
				const result = bg < 8 ? "dark" : "light";
				return result;
			}
		}
	}
	return "dark";
}

function getDefaultTheme(): string {
	return detectTerminalBackground();
}

// ============================================================================
// Global Theme Instance
// ============================================================================

// Use globalThis to share theme across module loaders (tsx + jiti in dev mode)
const THEME_KEY = Symbol.for("@kolisachint/hoocode-agent:theme");

// Export theme as a getter that reads from globalThis
// This ensures all module instances (tsx, jiti) see the same theme
export const theme: Theme = new Proxy({} as Theme, {
	get(_target, prop) {
		const t = (globalThis as Record<symbol, Theme>)[THEME_KEY];
		if (!t) throw new Error("Theme not initialized. Call initTheme() first.");
		return (t as unknown as Record<string | symbol, unknown>)[prop];
	},
});

function setGlobalTheme(t: Theme): void {
	(globalThis as Record<symbol, Theme>)[THEME_KEY] = t;
}

let currentThemeName: string | undefined;
let themeWatcher: fs.FSWatcher | undefined;
let themeReloadTimer: NodeJS.Timeout | undefined;
let onThemeChangeCallback: (() => void) | undefined;
const registeredThemes = new Map<string, Theme>();

export function setRegisteredThemes(themes: Theme[]): void {
	registeredThemes.clear();
	for (const theme of themes) {
		if (theme.name) {
			registeredThemes.set(theme.name, theme);
		}
	}
}

export function initTheme(themeName?: string, enableWatcher: boolean = false): void {
	const requested = themeName ?? getDefaultTheme();
	// A retired name loads as its successor (see RETIRED_THEMES), so record the
	// successor as the current theme too — otherwise the picker would have no row
	// to mark as current and `/theme` would report a name that is no longer real.
	const name = resolveThemeName(requested);
	currentThemeName = name;
	try {
		setGlobalTheme(loadTheme(name));
		if (enableWatcher) {
			startThemeWatcher();
		}
	} catch (_error) {
		// Theme is invalid - fall back to dark theme silently
		currentThemeName = "dark";
		setGlobalTheme(loadTheme("dark"));
		// Don't start watcher for fallback theme
	}
}

export function setTheme(requested: string, enableWatcher: boolean = false): { success: boolean; error?: string } {
	const name = resolveThemeName(requested);
	currentThemeName = name;
	try {
		setGlobalTheme(loadTheme(name));
		if (enableWatcher) {
			startThemeWatcher();
		}
		if (onThemeChangeCallback) {
			onThemeChangeCallback();
		}
		return { success: true };
	} catch (error) {
		// Theme is invalid - fall back to dark theme
		currentThemeName = "dark";
		setGlobalTheme(loadTheme("dark"));
		// Don't start watcher for fallback theme
		return {
			success: false,
			error: error instanceof Error ? error.message : String(error),
		};
	}
}

export function setThemeInstance(themeInstance: Theme): void {
	setGlobalTheme(themeInstance);
	currentThemeName = "<in-memory>";
	stopThemeWatcher(); // Can't watch a direct instance
	if (onThemeChangeCallback) {
		onThemeChangeCallback();
	}
}

export function onThemeChange(callback: () => void): void {
	onThemeChangeCallback = callback;
}

function startThemeWatcher(): void {
	stopThemeWatcher();

	// Only watch if it's a custom theme (not built-in)
	if (!currentThemeName || isBuiltinTheme(currentThemeName)) {
		return;
	}

	const customThemesDir = getCustomThemesDir();
	const watchedThemeName = currentThemeName;
	const watchedFileName = `${watchedThemeName}.json`;
	const themeFile = path.join(customThemesDir, watchedFileName);

	// Only watch if the file exists
	if (!fs.existsSync(themeFile)) {
		return;
	}

	const scheduleReload = () => {
		if (themeReloadTimer) {
			clearTimeout(themeReloadTimer);
		}
		themeReloadTimer = setTimeout(() => {
			themeReloadTimer = undefined;

			// Ignore stale timers after switching themes or stopping the watcher
			if (currentThemeName !== watchedThemeName) {
				return;
			}

			// Keep the last successfully loaded theme active if the file is temporarily missing
			if (!fs.existsSync(themeFile)) {
				return;
			}

			try {
				// Reload the theme from disk and refresh the registry cache
				const reloadedTheme = loadThemeFromPath(themeFile);
				registeredThemes.set(watchedThemeName, reloadedTheme);
				setGlobalTheme(reloadedTheme);
				// Notify callback (to invalidate UI)
				if (onThemeChangeCallback) {
					onThemeChangeCallback();
				}
			} catch (_error) {
				// Ignore errors (file might be in invalid state while being edited)
			}
		}, 100);
	};

	themeWatcher =
		watchWithErrorHandler(
			customThemesDir,
			(_eventType, filename) => {
				if (currentThemeName !== watchedThemeName) {
					return;
				}
				if (!filename) {
					scheduleReload();
					return;
				}
				if (filename !== watchedFileName) {
					return;
				}
				scheduleReload();
			},
			() => {
				closeWatcher(themeWatcher);
				themeWatcher = undefined;
			},
		) ?? undefined;
}

export function stopThemeWatcher(): void {
	if (themeReloadTimer) {
		clearTimeout(themeReloadTimer);
		themeReloadTimer = undefined;
	}
	closeWatcher(themeWatcher);
	themeWatcher = undefined;
}

// ============================================================================
// HTML Export Helpers
// ============================================================================

/**
 * Convert a 256-color index to hex string.
 * Indices 0-15: basic colors (approximate)
 * Indices 16-231: 6x6x6 color cube
 * Indices 232-255: grayscale ramp
 */
function ansi256ToHex(index: number): string {
	// Basic colors (0-15) - approximate common terminal values
	const basicColors = [
		"#000000",
		"#800000",
		"#008000",
		"#808000",
		"#000080",
		"#800080",
		"#008080",
		"#c0c0c0",
		"#808080",
		"#ff0000",
		"#00ff00",
		"#ffff00",
		"#0000ff",
		"#ff00ff",
		"#00ffff",
		"#ffffff",
	];
	if (index < 16) {
		return basicColors[index];
	}

	// Color cube (16-231): 6x6x6 = 216 colors
	if (index < 232) {
		const cubeIndex = index - 16;
		const r = Math.floor(cubeIndex / 36);
		const g = Math.floor((cubeIndex % 36) / 6);
		const b = cubeIndex % 6;
		const toHex = (n: number) => (n === 0 ? 0 : 55 + n * 40).toString(16).padStart(2, "0");
		return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
	}

	// Grayscale (232-255): 24 shades
	const gray = 8 + (index - 232) * 10;
	const grayHex = gray.toString(16).padStart(2, "0");
	return `#${grayHex}${grayHex}${grayHex}`;
}

/** WCAG relative luminance (0-1) of a resolved color, or undefined if unparseable. */
function relativeLuminance(color: string | number): number | undefined {
	const hex = typeof color === "number" ? ansi256ToHex(color) : color;
	if (!hex.startsWith("#")) return undefined;
	let rgb: { r: number; g: number; b: number };
	try {
		rgb = hexToRgb(hex);
	} catch {
		return undefined;
	}
	const toLinear = (c: number) => {
		const s = c / 255;
		return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
}

/**
 * Whether a theme sits on a light backdrop, judged by its own colors rather
 * than its name — `light` is no longer the only light-background theme.
 */
function hasLightBackdrop(themeJson: ThemeJson, resolved: Record<string, string | number>): boolean {
	const pageBg = themeJson.export?.pageBg;
	const candidates = [
		pageBg === undefined ? undefined : resolveVarRefs(pageBg, themeJson.vars ?? {}),
		resolved.userMessageBg,
	];
	for (const candidate of candidates) {
		if (candidate === undefined || candidate === "") continue;
		const luminance = relativeLuminance(candidate);
		if (luminance !== undefined) return luminance > 0.5;
	}
	return false;
}

/**
 * Get resolved theme colors as CSS-compatible hex strings.
 * Used by HTML export to generate CSS custom properties.
 */
export function getResolvedThemeColors(themeName?: string): Record<string, string> {
	const name = themeName ?? currentThemeName ?? getDefaultTheme();
	const themeJson = loadThemeJson(name);
	const resolved = resolveThemeColors(themeJson.colors, themeJson.vars);
	const isLight = hasLightBackdrop(themeJson, resolved);

	// Default text color for empty values (terminal uses default fg color)
	const defaultText = isLight ? "#000000" : "#e5e5e7";

	const cssColors: Record<string, string> = {};
	for (const [key, value] of Object.entries(resolved)) {
		if (typeof value === "number") {
			cssColors[key] = ansi256ToHex(value);
		} else if (value === "") {
			// Empty means default terminal color - use sensible fallback for HTML
			cssColors[key] = defaultText;
		} else {
			cssColors[key] = value;
		}
	}
	return cssColors;
}

/**
 * Get explicit export colors from theme JSON, if specified.
 * Returns undefined for each color that isn't explicitly set.
 */
export function getThemeExportColors(themeName?: string): {
	pageBg?: string;
	cardBg?: string;
	infoBg?: string;
} {
	const name = themeName ?? currentThemeName ?? getDefaultTheme();
	try {
		const themeJson = loadThemeJson(name);
		const exportSection = themeJson.export;
		if (!exportSection) return {};

		const vars = themeJson.vars ?? {};
		const resolve = (value: ColorValue | undefined): string | undefined => {
			if (value === undefined) return undefined;
			const resolved = resolveVarRefs(value, vars);
			if (typeof resolved === "number") return ansi256ToHex(resolved);
			if (resolved === "") return undefined;
			return resolved;
		};

		return {
			pageBg: resolve(exportSection.pageBg),
			cardBg: resolve(exportSection.cardBg),
			infoBg: resolve(exportSection.infoBg),
		};
	} catch {
		return {};
	}
}

// ============================================================================
// TUI Helpers
// ============================================================================

type CliHighlightTheme = Record<string, (s: string) => string>;

let cachedHighlightThemeFor: Theme | undefined;
let cachedCliHighlightTheme: CliHighlightTheme | undefined;

function buildCliHighlightTheme(t: Theme): CliHighlightTheme {
	return {
		keyword: (s: string) => t.fg("syntaxKeyword", s),
		built_in: (s: string) => t.fg("syntaxType", s),
		literal: (s: string) => t.fg("syntaxNumber", s),
		number: (s: string) => t.fg("syntaxNumber", s),
		string: (s: string) => t.fg("syntaxString", s),
		comment: (s: string) => t.fg("syntaxComment", s),
		function: (s: string) => t.fg("syntaxFunction", s),
		title: (s: string) => t.fg("syntaxFunction", s),
		class: (s: string) => t.fg("syntaxType", s),
		type: (s: string) => t.fg("syntaxType", s),
		attr: (s: string) => t.fg("syntaxVariable", s),
		variable: (s: string) => t.fg("syntaxVariable", s),
		params: (s: string) => t.fg("syntaxVariable", s),
		operator: (s: string) => t.fg("syntaxOperator", s),
		punctuation: (s: string) => t.fg("syntaxPunctuation", s),
	};
}

function getCliHighlightTheme(t: Theme): CliHighlightTheme {
	if (cachedHighlightThemeFor !== t || !cachedCliHighlightTheme) {
		cachedHighlightThemeFor = t;
		cachedCliHighlightTheme = buildCliHighlightTheme(t);
	}
	return cachedCliHighlightTheme;
}

/**
 * Highlight code with syntax coloring based on file extension or language.
 * Returns array of highlighted lines.
 */
export function highlightCode(code: string, lang?: string): string[] {
	// Validate language before highlighting to avoid stderr spam from cli-highlight
	const validLang = lang && supportsLanguage(lang) ? lang : undefined;
	// Skip highlighting when no valid language is specified. cli-highlight's
	// auto-detection is unreliable and can misidentify prose as AppleScript,
	// LiveCodeServer, etc., coloring random English words as keywords.
	if (!validLang) {
		return code.split("\n").map((line) => theme.fg("mdCodeBlock", line));
	}
	const opts = {
		language: validLang,
		ignoreIllegals: true,
		theme: getCliHighlightTheme(theme),
	};
	try {
		return highlight(code, opts).split("\n");
	} catch {
		return code.split("\n");
	}
}

/**
 * Get language identifier from file path extension.
 */
export function getLanguageFromPath(filePath: string): string | undefined {
	const ext = filePath.split(".").pop()?.toLowerCase();
	if (!ext) return undefined;

	const extToLang: Record<string, string> = {
		ts: "typescript",
		tsx: "typescript",
		js: "javascript",
		jsx: "javascript",
		mjs: "javascript",
		cjs: "javascript",
		py: "python",
		rb: "ruby",
		rs: "rust",
		go: "go",
		java: "java",
		kt: "kotlin",
		swift: "swift",
		c: "c",
		h: "c",
		cpp: "cpp",
		cc: "cpp",
		cxx: "cpp",
		hpp: "cpp",
		cs: "csharp",
		php: "php",
		sh: "bash",
		bash: "bash",
		zsh: "bash",
		fish: "fish",
		ps1: "powershell",
		sql: "sql",
		html: "html",
		htm: "html",
		css: "css",
		scss: "scss",
		sass: "sass",
		less: "less",
		json: "json",
		yaml: "yaml",
		yml: "yaml",
		toml: "toml",
		xml: "xml",
		md: "markdown",
		markdown: "markdown",
		dockerfile: "dockerfile",
		makefile: "makefile",
		cmake: "cmake",
		lua: "lua",
		perl: "perl",
		r: "r",
		scala: "scala",
		clj: "clojure",
		ex: "elixir",
		exs: "elixir",
		erl: "erlang",
		hs: "haskell",
		ml: "ocaml",
		vim: "vim",
		graphql: "graphql",
		proto: "protobuf",
		tf: "hcl",
		hcl: "hcl",
	};

	return extToLang[ext];
}

/**
 * The `[branch]` / `[skill]` / `[extension]` tag that heads a message block.
 *
 * Five call sites had written the same bold-brackets-in-customMessageLabel
 * string by hand, which is why they are here now: a theme that sets the tape
 * pair renders the tag as a strip laid across the block instead, and that only
 * reads as one device if every tag agrees. Themes without the pair get exactly
 * the string the call sites used to build.
 */
export function messageLabel(name: string): string {
	if (theme.hasBg("tapeBg") && theme.has("tapeText")) {
		return theme.bg("tapeBg", theme.fg("tapeText", `\x1b[1m ${name} \x1b[22m`));
	}
	return theme.fg("customMessageLabel", `\x1b[1m[${name}]\x1b[22m`);
}

/**
 * The shadow pass for a filled block, or nothing when the theme has no
 * `paperShadow`. Every block that reads as a pasted sheet — a user message, an
 * extension message, an error or warning notice — takes its shadow from here so
 * they cannot drift apart.
 */
export function getPaperShadowFn(): ((text: string) => string) | undefined {
	return theme.has("paperShadow") ? (text: string) => theme.fg("paperShadow", text) : undefined;
}

/**
 * How far a sheet holds back from the right margin, leaving a gutter of page.
 *
 * One column, which is the whole of what the treatment needs and no more. The
 * gutter exists so the sheet has a right edge to show and somewhere to put the
 * shadow's column; the column is `▏`, a *left one-eighth* block, so it paints
 * the sheet's edge as a hairline at the left of that one cell and leaves the
 * rest as page. Three columns bought nothing the first one does not already
 * give and cost two columns of every message, on every line, forever.
 */
export const PAPER_INSET = 1;

/**
 * Give a filled block the paper treatment: a gutter of page at its right, and a
 * shadow down that edge and along the bottom.
 *
 * The two arrive together because they are one decision. A block that runs edge
 * to edge has no right edge to show and nowhere to cast a shadow, so a theme
 * asking for paper is asking for the gutter that makes paper possible.
 * A theme without `paperShadow` gets the full-width band it has always drawn.
 *
 * The decision is handed to the box as a function, not as an answer, because
 * blocks outlive themes: the messages already on screen when the user switches
 * theme keep the boxes they were built with. Answering once meant a message
 * pasted under a cut-out theme went on asking a plain theme for `paperShadow`,
 * a token that theme never defined — which threw rather than rendering.
 */
export function applyPaperSheet(box: Box): void {
	box.setPaper(() => {
		const shadow = getPaperShadowFn();
		return shadow ? { shadow, inset: PAPER_INSET } : undefined;
	});
}

/**
 * The fills that make a *block* — a pasted sheet of message — as opposed to a
 * chip or a row stroke.
 *
 * `selectedBg` and `activeToolBg` mark part of a row and `brandBg`, `headlineBg`
 * and `tapeBg` are chips laid on top of one; none of them is a sheet and none
 * takes a shadow. These four are, and every one of them has to look like the
 * others or the page stops reading as paper.
 */
export type BlockFill = "userMessageBg" | "customMessageBg" | "warningBg" | "toolErrorBg";

/**
 * Fill a message block, and give it the edge that goes with the fill.
 *
 * These are one decision, so they are made in one call. Kept apart they drifted
 * exactly as you would expect: `[skill]`, `[compaction]` and `[branch]` blocks
 * were each written to use "the same background colour as custom messages for
 * visual consistency" and each stopped there, so under a cut-out theme they
 * rendered as flat full-width bands beside the sheets they were copying —
 * same paint, no gutter, no edge, no shadow, in the same transcript.
 *
 * A theme with no `paperShadow` still gets the full-width band it always had:
 * `applyPaperSheet` resolves that per frame, so this is safe on every theme and
 * survives a theme switch under blocks already on screen.
 */
export function applyBlockFill(box: Box, fill: BlockFill): void {
	box.setBgFn((text: string) => theme.bg(fill, text));
	applyPaperSheet(box);
}

export function getMarkdownTheme(): MarkdownTheme {
	// A theme that sets the headline pair renders headings as a filled chip: the
	// text takes the chip's ink, and the fill is applied to the finished line so
	// the chip's padding never leaks into the inline-style prefix.
	//
	// Only the top two levels get a bar. Below that the renderer keeps the `###`
	// marker, and a marker inside a filled chip is saying the same thing twice —
	// the chip *is* the marker. A page of `###` bars is also just loud: two
	// levels of chip and the rest as coloured text is the hierarchy a printed
	// page would use.
	//
	// The pair is looked up per call rather than once here: a markdown theme is
	// built with the message that holds it and is still in use after the user
	// switches theme, so a decision frozen at build time would have that message
	// asking a plain theme for `headlineText`, which it never defined.
	const chipped = (level: number) => level <= 2 && theme.hasBg("headlineBg") && theme.has("headlineText");
	return {
		heading: (text: string, level: number) => theme.fg(chipped(level) ? "headlineText" : "mdHeading", text),
		headingBlock: (line: string, level: number) => (chipped(level) ? theme.bg("headlineBg", ` ${line} `) : line),
		link: (text: string) => theme.fg("mdLink", text),
		linkUrl: (text: string) => theme.fg("mdLinkUrl", text),
		code: (text: string) => theme.fg("mdCode", text),
		codeBlock: (text: string) => theme.fg("mdCodeBlock", text),
		codeBlockBorder: (text: string) => theme.fg("mdCodeBlockBorder", text),
		quote: (text: string) => theme.fg("mdQuote", text),
		quoteBorder: (text: string) => theme.fg("mdQuoteBorder", text),
		hr: (text: string) => theme.fg("mdHr", text),
		listBullet: (text: string) => theme.fg("mdListBullet", text),
		bold: (text: string) => theme.bold(text),
		italic: (text: string) => theme.italic(text),
		underline: (text: string) => theme.underline(text),
		strikethrough: (text: string) => chalk.strikethrough(text),
		highlightCode: (code: string, lang?: string): string[] => {
			// Validate language before highlighting to avoid stderr spam from cli-highlight
			const validLang = lang && supportsLanguage(lang) ? lang : undefined;
			// Skip highlighting when no valid language is specified. cli-highlight's
			// auto-detection is unreliable and can misidentify prose as AppleScript,
			// LiveCodeServer, etc., coloring random English words as keywords.
			if (!validLang) {
				return code.split("\n").map((line) => theme.fg("mdCodeBlock", line));
			}
			const opts = {
				language: validLang,
				ignoreIllegals: true,
				theme: getCliHighlightTheme(theme),
			};
			try {
				return highlight(code, opts).split("\n");
			} catch {
				return code.split("\n").map((line) => theme.fg("mdCodeBlock", line));
			}
		},
	};
}

/**
 * The cursor every picker marks its selected row with, trailing space included.
 * Pickers used to pick their own — `→`, `›` and `>` were all in use, and the
 * shared `SelectList` hardcoded a fourth — so which glyph you saw depended on
 * which list you had opened.
 */
export const SELECT_CURSOR = "› ";

/**
 * Dress an input line's caret in the session theme. Every place you can type —
 * a picker's query, the login dialog, an extension's prompt, a rename — is the
 * same signal as the main prompt, so it wears the prompt's `❯` (the `Input`
 * default) rather than a glyph of its own. Muted, because an input line usually
 * sits a row or two under an accent `›` marking a selected row and must not
 * out-shout it.
 *
 * Mutates rather than returning a theme object because `Input` takes no theme;
 * the colour is resolved at render, so a theme swap follows. Returns the input
 * so it can wrap a `new Input()` in a field initializer.
 */
export function styleInput(input: Input): Input {
	input.promptColor = (text: string) => theme.fg("muted", text);
	return input;
}

/**
 * The blank gutter an unselected row is indented by. Derived from the cursor
 * rather than hardcoded, so a cursor of a different width keeps the column it
 * marks in the same place instead of shifting every unselected row by one.
 */
export const SELECT_GUTTER = " ".repeat(visibleWidth(SELECT_CURSOR));

/**
 * Paint a selected row as a filled band, padding it to the full width first so
 * the highlight reaches the right edge instead of stopping wherever the text
 * happens to stop. For components that render their own rows; `SelectList` and
 * `SettingsList` get the same treatment through their `selectedRow` theme hook.
 *
 * Pass a row that already fits the width. A row long enough to have been
 * truncated carries a reset at the cut, which ends the band on the ellipsis.
 */
export function paintSelectedRow(line: string, width: number): string {
	return applyBackgroundToLine(line, width, (text: string) => theme.bg("selectedBg", text));
}

export function getSelectListTheme(): SelectListTheme {
	return {
		selectedPrefix: (text: string) => theme.fg("accent", text),
		selectedText: (text: string) => theme.fg("accent", text),
		description: (text: string) => theme.fg("muted", text),
		scrollInfo: (text: string) => theme.fg("muted", text),
		noMatch: (text: string) => theme.fg("muted", text),
		// Left unstyled: SelectList passes the cursor through `selectedText`
		// with the rest of the row.
		cursor: SELECT_CURSOR,
		selectedRow: (text: string) => theme.bg("selectedBg", text),
	};
}

export function getEditorTheme(): EditorTheme {
	return {
		borderColor: (text: string) => theme.fg("borderMuted", text),
		selectList: getSelectListTheme(),
	};
}

export function getSettingsListTheme(): SettingsListTheme {
	return {
		label: (text: string, selected: boolean) => (selected ? theme.fg("accent", text) : text),
		value: (text: string, selected: boolean) => (selected ? theme.fg("accent", text) : theme.fg("muted", text)),
		description: (text: string) => theme.fg("dim", text),
		cursor: theme.fg("accent", SELECT_CURSOR),
		hint: (text: string) => theme.fg("dim", text),
		selectedRow: (text: string) => theme.bg("selectedBg", text),
	};
}
